/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

/**
 * Walks the records of a FIT byte array, one message at a time.
 *
 * Pull-based on purpose: every public decode idiom this SDK offers (a result
 * object, a callback, a Sequence) is a thin wrapper over this single iterator,
 * so they cannot drift apart. A caller who wants a Flow can wrap the Sequence
 * in one line, which is why the SDK does not depend on coroutines to provide it.
 *
 * Handles chained files, where several complete FIT files are concatenated into
 * one stream.
 */
internal class MesgCursor(
    private val reader: ByteReader,
    private val options: DecodeOptions,
) : Iterator<Mesg> {
    private val localDefinitions = arrayOfNulls<MesgDefinition>(Fit.NUM_LOCAL_MESG_DEFS)
    private val accumulator = Accumulator()

    val developerData: DeveloperDataLookup = DeveloperDataLookup()

    /** Profile version from the first file header met, if any. */
    var profileVersion: UShort? = null
        private set

    private var mesgIndex = 0
    private var fileEnd = -1
    private var pending: Mesg? = null

    override fun hasNext(): Boolean {
        if (pending != null) return true
        pending = advance()
        return pending != null
    }

    override fun next(): Mesg {
        val mesg = pending ?: advance() ?: throw NoSuchElementException("no more FIT messages")
        pending = null
        return mesg
    }

    private fun advance(): Mesg? {
        while (true) {
            if (fileEnd >= 0 && reader.position >= fileEnd) endFile()
            if (!reader.hasBytesAvailable) return null
            if (fileEnd < 0) beginFile()
            if (!reader.hasBytesAvailable) return null

            decodeRecord()?.let { return it }
        }
    }

    // ------------------------------------------------------------- file framing

    private fun beginFile() {
        if (options.mode == DecodeMode.DATA_ONLY) {
            // No header and no CRC: the records run to the end of the input.
            fileEnd = reader.size
            return
        }

        val start = reader.position
        reader.crc = Crc()
        val header = FileHeader.read(reader)

        if (options.mode == DecodeMode.NORMAL) {
            if (!header.isValid) throw FitFormatException("input is not a FIT file", start)
            if (profileVersion == null) profileVersion = header.profileVersion

            val end = start + header.headerSize + header.dataSize.toInt()
            // A header claiming more data than the file holds would run the
            // record loop off the end; clamp and let the CRC report it.
            fileEnd = if (end in 0..reader.size) end else reader.size
            return
        }

        // SKIP_HEADER: the header is there but nothing it says is trusted, so
        // fall back to the standard size and read records to the end of the
        // input. Chained files are not detectable this way, by construction.
        if (profileVersion == null && header.isValid) profileVersion = header.profileVersion

        val headerSize = if (header.headerSize == Fit.HEADER_WITH_CRC_SIZE || header.headerSize == Fit.HEADER_WITHOUT_CRC_SIZE) {
            header.headerSize
        } else {
            Fit.HEADER_WITH_CRC_SIZE
        }
        // The declared header size is not trusted either, so a stream shorter
        // than it claims lands at the end of the input rather than erroring.
        reader.seek((start + headerSize).coerceIn(reader.position, reader.size))

        val declaredEnd = start + headerSize + header.dataSize.toInt()
        fileEnd = if (header.dataSize > 0u && declaredEnd <= reader.size) {
            declaredEnd
        } else {
            (reader.size - Fit.CRC_SIZE).coerceAtLeast(reader.position)
        }
    }

    private fun endFile() {
        val calculated = reader.crc?.value
        reader.crc = null

        if (options.mode != DecodeMode.DATA_ONLY) {
            if (reader.bytesAvailable >= Fit.CRC_SIZE) {
                val recorded = reader.readUShort()
                if (options.mode == DecodeMode.NORMAL && recorded != calculated) {
                    fileEnd = -1
                    throw FitFormatException("CRC mismatch: file says $recorded, computed $calculated", reader.position - 2)
                }
            } else if (options.mode == DecodeMode.NORMAL) {
                // Every FIT file ends with a two-byte CRC, so a stream that runs
                // out before it was truncated: reporting it is the only way a
                // caller can tell a whole file from most of one. In a chained
                // stream this can only be the last file, the earlier ones having
                // been followed by more bytes. SKIP_HEADER cannot tell where a
                // file ends, and DATA_ONLY expects no CRC at all, so neither
                // mode complains.
                val available = reader.bytesAvailable
                fileEnd = -1
                throw FitFormatException(
                    "file ends before its CRC: $available of ${Fit.CRC_SIZE} bytes left",
                    reader.position,
                )
            }
        }

        // A chained file starts right here; -1 makes the next advance() read
        // another header.
        fileEnd = -1
        localDefinitions.fill(null)
    }

    // ----------------------------------------------------------------- records

    /** Reads one record, returning a message only when the record was a data record. */
    private fun decodeRecord(): Mesg? {
        val recordHeader = reader.peekByte().toInt()

        if ((recordHeader and Fit.COMPRESSED_HEADER_MASK) == Fit.COMPRESSED_HEADER_MASK) {
            throw FitFormatException("compressed timestamp data messages are not supported", reader.position)
        }

        if ((recordHeader and Fit.MESG_DEFINITION_MASK) == Fit.MESG_DEFINITION_MASK) {
            val header = reader.readByte()
            val definition = MesgDefinition.read(reader, header)
            localDefinitions[definition.localMesgNum.toInt()] = definition
            return null
        }

        return decodeDataMesg(reader.readByte())
    }

    private fun decodeDataMesg(recordHeader: UByte): Mesg? {
        val local = recordHeader.toInt() and Fit.LOCAL_MESG_NUM_MASK
        val definition = localDefinitions[local]
            ?: throw FitFormatException("data record uses local message $local, which nothing defined", reader.position - 1)

        val known = Factory.isKnownMesg(definition.globalMesgNum)

        // Start empty rather than from the profile prototype: read() creates
        // each field as the definition names it, which both preserves the
        // producer's field order and avoids copying ~50 unused Field objects
        // per message.
        val mesg = Mesg(Factory.mesgName(definition.globalMesgNum), definition.globalMesgNum)
        mesg.localMesgNum = definition.localMesgNum
        mesg.decoderMesgIndex = mesgIndex++

        mesg.read(
            reader = reader,
            definition = definition,
            accumulator = accumulator,
            expandComponents = options.expandComponents,
            developerFields = { developerData.createField(it) },
        )

        registerDeclarations(mesg)

        if (!known && !options.includeUnknownData) return null

        // A field whose every value was the invalid sentinel stops being
        // "present". typed() then adopts the fields rather than copying them:
        // this mesg was built for this record and nothing else holds it.
        mesg.dropEmptyFields()
        return Factory.typed(mesg)
    }

    /**
     * Picks up the two message types that change how later records decode.
     */
    private fun registerDeclarations(mesg: Mesg) {
        when (mesg.globalMesgNum) {
            Profile.MesgNum.DEVELOPER_DATA_ID -> developerData.addDeveloperDataId(DeveloperDataIdMesg(mesg))
            Profile.MesgNum.FIELD_DESCRIPTION -> developerData.addFieldDescription(FieldDescriptionMesg(mesg))
        }
    }
}
